import { ref } from 'vue';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  deleteDoc,
  updateDoc,
  orderBy,
  query,
  Timestamp,
  type DocumentData,
  type QueryDocumentSnapshot
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuth } from './useAuth';
import { useToast } from './useToast';
import { extractPlainText, sanitizeRichHtml } from '@/utils/sanitizeHtml';

export interface NewsItem {
  id: string;
  title: string;
  content: string;
  createdAt: Date;
  author: string;
  featured?: boolean;
  categories?: string[];
}

const news = ref<NewsItem[]>([]);
const loading = ref(false);

export function useNews() {
  const { user } = useAuth();
  const { success, error } = useToast();

  const newsCollection = collection(db, 'news');

  const fetchNews = async (): Promise<void> => {
    try {
      loading.value = true;
      const q = query(newsCollection, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);

      news.value = querySnapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title,
          content: data.content,
          createdAt: data.createdAt?.toDate() || new Date(),
          author: data.author,
          featured: data.featured || false,
          categories: data.categories || [],
        };
      });
    } catch (err) {
      console.error('Error fetching news:', err);
      error('Error al cargar las noticias', 'Error');
    } finally {
      loading.value = false;
    }
  };

  const createNews = async (title: string, content: string, createdAt?: Date): Promise<boolean> => {
    if (!user.value) {
      error('Debes estar autenticado para crear noticias', 'Error de autenticación');
      return false;
    }

    if (!title.trim() || !content.trim()) {
      error('El título y contenido son obligatorios', 'Campos requeridos');
      return false;
    }

    try {
      const sanitizedContent = sanitizeRichHtml(content.trim());
      if (!extractPlainText(sanitizedContent)) {
        error('El contenido no puede estar vacío', 'Campos requeridos');
        return false;
      }

      loading.value = true;
      await addDoc(newsCollection, {
        title: title.trim(),
        content: sanitizedContent,
        createdAt: createdAt ? Timestamp.fromDate(createdAt) : Timestamp.now(),
        author: user.value.displayName?.trim() || 'Administración',
      });

      success('Noticia creada correctamente', 'Éxito');
      await fetchNews(); // Recargar la lista de noticias
      return true;
    } catch (err) {
      console.error('Error creating news:', err);
      error('Error al crear la noticia', 'Error');
      return false;
    } finally {
      loading.value = false;
    }
  };

  const updateNews = async (id: string, title: string, content: string, createdAt?: Date): Promise<boolean> => {
    if (!user.value) {
      error('Debes estar autenticado para editar noticias', 'Error de autenticación');
      return false;
    }

    if (!title.trim() || !content.trim()) {
      error('El título y contenido son obligatorios', 'Campos requeridos');
      return false;
    }

    try {
      const sanitizedContent = sanitizeRichHtml(content.trim());
      if (!extractPlainText(sanitizedContent)) {
        error('El contenido no puede estar vacío', 'Campos requeridos');
        return false;
      }

      loading.value = true;
      const newsDoc = doc(db, 'news', id);
      const updateData: any = {
        title: title.trim(),
        content: sanitizedContent,
      };

      if (createdAt) {
        updateData.createdAt = Timestamp.fromDate(createdAt);
      }

      await updateDoc(newsDoc, updateData);

      success('Noticia actualizada correctamente', 'Éxito');
      await fetchNews(); // Recargar la lista de noticias
      return true;
    } catch (err) {
      console.error('Error updating news:', err);
      error('Error al actualizar la noticia', 'Error');
      return false;
    } finally {
      loading.value = false;
    }
  };

  const deleteNews = async (id: string): Promise<boolean> => {
    if (!user.value) {
      error('Debes estar autenticado para eliminar noticias', 'Error de autenticación');
      return false;
    }

    try {
      loading.value = true;
      await deleteDoc(doc(db, 'news', id));
      success('Noticia eliminada correctamente', 'Éxito');
      await fetchNews(); // Recargar la lista de noticias
      return true;
    } catch (err) {
      console.error('Error deleting news:', err);
      error('Error al eliminar la noticia', 'Error');
      return false;
    } finally {
      loading.value = false;
    }
  };

  const getNewsById = (id: string): NewsItem | undefined => {
    return news.value.find(item => item.id === id);
  };

  return {
    news,
    loading,
    fetchNews,
    createNews,
    updateNews,
    deleteNews,
    getNewsById,
  };
}
